<template>
    <PopoverRoot v-model:open="open">
        <PopoverTrigger
            class="border-input dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center gap-2 rounded-md border bg-white px-3 text-left text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
            <CalendarIcon
                class="text-muted-foreground size-4 shrink-0"
                aria-hidden="true"
            />
            <span
                class="block flex-1 truncate"
                :class="display ? '' : 'text-muted-foreground'"
            >
                {{ display || placeholderText }}
            </span>
            <button
                v-if="display"
                type="button"
                class="text-muted-foreground shrink-0 rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
                aria-label="Limpar datas"
                @click.stop="clear"
            >
                <XMarkIcon class="size-3.5" aria-hidden="true" />
            </button>
        </PopoverTrigger>

        <PopoverPortal>
            <PopoverContent
                align="start"
                :side-offset="6"
                class="bg-popover text-popover-foreground z-50 rounded-md border p-3 shadow-md outline-none"
            >
                <div v-if="monthMode" class="w-56">
                    <div class="mb-2 flex items-center justify-between">
                        <button
                            type="button"
                            class="hover:bg-accent rounded-md p-1.5"
                            @click="pickerYear--"
                        >
                            <ChevronLeftIcon class="size-4" />
                        </button>
                        <span class="text-sm font-medium">{{
                            pickerYear
                        }}</span>
                        <button
                            type="button"
                            class="hover:bg-accent rounded-md p-1.5"
                            @click="pickerYear++"
                        >
                            <ChevronRightIcon class="size-4" />
                        </button>
                    </div>
                    <div class="grid grid-cols-3 gap-1">
                        <button
                            v-for="(name, index) in monthNames"
                            :key="index"
                            type="button"
                            class="rounded-md px-2 py-1.5 text-sm capitalize"
                            :class="
                                isSelectedMonth(index)
                                    ? 'bg-indigo-500 text-white'
                                    : 'hover:bg-accent'
                            "
                            @click="pickMonth(index)"
                        >
                            {{ name }}
                        </button>
                    </div>
                </div>

                <RangeCalendarRoot
                    v-else
                    v-slot="{ grid, weekDays }"
                    v-model="calendarRange"
                    :number-of-months="2"
                    :locale="'pt-BR'"
                    fixed-weeks
                    class="flex gap-6"
                >
                    <RangeCalendarGrid
                        v-for="month in grid"
                        :key="month.value.toString()"
                        class="select-none"
                    >
                        <RangeCalendarHeader
                            class="mb-2 flex items-center justify-between"
                        >
                            <RangeCalendarPrev
                                v-if="month === grid[0]"
                                class="hover:bg-accent rounded-md p-1.5"
                            >
                                <ChevronLeftIcon class="size-4" />
                            </RangeCalendarPrev>
                            <span v-else class="w-7"></span>
                            <div
                                class="text-sm font-medium capitalize"
                            >
                                {{ headingFor(month.value) }}
                            </div>
                            <RangeCalendarNext
                                v-if="month === grid[grid.length - 1]"
                                class="hover:bg-accent rounded-md p-1.5"
                            >
                                <ChevronRightIcon class="size-4" />
                            </RangeCalendarNext>
                            <span v-else class="w-7"></span>
                        </RangeCalendarHeader>
                        <RangeCalendarGridHead>
                            <RangeCalendarGridRow class="flex">
                                <RangeCalendarHeadCell
                                    v-for="day in weekDays"
                                    :key="day"
                                    class="text-muted-foreground w-8 text-center text-[11px] font-normal capitalize"
                                >
                                    {{ day }}
                                </RangeCalendarHeadCell>
                            </RangeCalendarGridRow>
                        </RangeCalendarGridHead>
                        <RangeCalendarGridBody>
                            <RangeCalendarGridRow
                                v-for="(weekDates, index) in month.rows"
                                :key="`week-${index}`"
                                class="flex"
                            >
                                <RangeCalendarCell
                                    v-for="weekDate in weekDates"
                                    :key="weekDate.toString()"
                                    :date="weekDate"
                                    class="p-0"
                                >
                                    <RangeCalendarCellTrigger
                                        :day="weekDate"
                                        :month="month.value"
                                        class="flex size-8 items-center justify-center rounded-md text-sm outline-none hover:bg-black/5 data-[disabled]:pointer-events-none data-[outside-view]:opacity-30 data-[selected]:bg-indigo-500/15 data-[selected]:text-indigo-600 data-[selection-end]:bg-indigo-500 data-[selection-end]:text-white data-[selection-start]:bg-indigo-500 data-[selection-start]:text-white data-[today]:font-semibold data-[today]:text-indigo-500 data-[selection-end]:data-[today]:text-white data-[selection-start]:data-[today]:text-white dark:hover:bg-white/10"
                                    />
                                </RangeCalendarCell>
                            </RangeCalendarGridRow>
                        </RangeCalendarGridBody>
                    </RangeCalendarGrid>
                </RangeCalendarRoot>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>

<script>
import {
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
    RangeCalendarCell,
    RangeCalendarCellTrigger,
    RangeCalendarGrid,
    RangeCalendarGridBody,
    RangeCalendarGridHead,
    RangeCalendarGridRow,
    RangeCalendarHeadCell,
    RangeCalendarHeader,
    RangeCalendarNext,
    RangeCalendarPrev,
    RangeCalendarRoot,
} from "reka-ui";
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";

const pad = (value) => String(value).padStart(2, "0");

export default {
    name: "DateRangePicker",

    components: {
        PopoverContent,
        PopoverPortal,
        PopoverRoot,
        PopoverTrigger,
        RangeCalendarCell,
        RangeCalendarCellTrigger,
        RangeCalendarGrid,
        RangeCalendarGridBody,
        RangeCalendarGridHead,
        RangeCalendarGridRow,
        RangeCalendarHeadCell,
        RangeCalendarHeader,
        RangeCalendarNext,
        RangeCalendarPrev,
        RangeCalendarRoot,
        CalendarIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        XMarkIcon,
    },

    props: {
        modelValue: { type: [Array, String], default: () => [] },
        asSingle: { type: Boolean, default: false },
        formatter: {
            type: Object,
            default: () => ({ date: "DD/MM/YYYY", month: "MMM" }),
        },
        shortcuts: { type: [Boolean, Object], default: false },
    },

    emits: ["update:modelValue", "input"],

    data() {
        return {
            open: false,
            pickerYear: today(getLocalTimeZone()).year,
            monthNames: [
                "jan",
                "fev",
                "mar",
                "abr",
                "mai",
                "jun",
                "jul",
                "ago",
                "set",
                "out",
                "nov",
                "dez",
            ],
        };
    },

    computed: {
        monthMode() {
            return this.asSingle && this.formatter.date === "MM/YYYY";
        },

        values() {
            if (Array.isArray(this.modelValue)) {
                return this.modelValue.filter(Boolean);
            }
            if (typeof this.modelValue === "string" && this.modelValue) {
                return this.modelValue.split(/\s*[,~]\s*/).filter(Boolean);
            }
            return [];
        },

        calendarRange: {
            get() {
                const [start, end] = this.values;
                return {
                    start: this.parseDate(start),
                    end: this.parseDate(end),
                };
            },
            set(range) {
                if (!range?.start || !range?.end) {
                    return;
                }
                const value = [
                    this.formatDate(range.start),
                    this.formatDate(range.end),
                ];
                this.$emit("update:modelValue", value);
                this.$emit("input", value);
                this.open = false;
            },
        },

        display() {
            if (this.monthMode) {
                return this.values[0] ?? "";
            }
            const [start, end] = this.values;
            if (!start || !end) {
                return "";
            }
            return `${start} ~ ${end}`;
        },

        placeholderText() {
            return this.monthMode ? "MM/AAAA" : "DD/MM/AAAA ~ DD/MM/AAAA";
        },
    },

    methods: {
        parseDate(value) {
            if (!value) {
                return undefined;
            }
            const [day, month, year] = value.split("/").map(Number);
            if (!day || !month || !year) {
                return undefined;
            }
            return new CalendarDate(year, month, day);
        },

        formatDate(date) {
            return `${pad(date.day)}/${pad(date.month)}/${date.year}`;
        },

        headingFor(monthValue) {
            return new Date(
                monthValue.year,
                monthValue.month - 1,
                1
            ).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
        },

        isSelectedMonth(index) {
            const [month, year] = (this.values[0] ?? "").split("/").map(Number);
            return month === index + 1 && year === this.pickerYear;
        },

        pickMonth(index) {
            const value = `${pad(index + 1)}/${this.pickerYear}`;
            this.$emit("update:modelValue", value);
            this.$emit("input", value);
            this.open = false;
        },

        clear() {
            const value = this.monthMode ? "" : [];
            this.$emit("update:modelValue", value);
            this.$emit("input", value);
        },
    },
};
</script>
